import { TILE, isqrt, tileCenter } from '../core/fixed';
import { inBounds, isBlocked, type GameMap } from './gameMap';
import type { OccupancyFn } from './unitGrid';

export interface TilePoint {
  readonly x: number;
  readonly y: number;
}

/** Integer step costs: 10 straight, 14 diagonal (≈ 10·√2). */
const STRAIGHT = 10;
const DIAGONAL = 14;

/** Neighbor order is fixed so ties resolve identically on every run. */
const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

/** Octile distance: the exact cost of the best path on an empty grid. Admissible. */
export function heuristic(ax: number, ay: number, bx: number, by: number): number {
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  return dx > dy ? DIAGONAL * dy + STRAIGHT * (dx - dy) : DIAGONAL * dx + STRAIGHT * (dy - dx);
}

/**
 * May a unit step from (x, y) by (dx, dy)? Diagonal steps are refused when either
 * orthogonal neighbor is blocked, so nothing squeezes between two forest tiles.
 */
export function canStep(
  map: GameMap,
  x: number,
  y: number,
  dx: number,
  dy: number,
  extra?: OccupancyFn,
): boolean {
  const blocked = (tx: number, ty: number) =>
    isBlocked(map, tx, ty) || (extra !== undefined && extra(tx, ty));
  if (blocked(x + dx, y + dy)) return false;
  if (dx !== 0 && dy !== 0) {
    if (blocked(x + dx, y) || blocked(x, y + dy)) return false;
  }
  return true;
}

/**
 * Is the straight segment between two world-unit points clear of blocked tiles?
 * Exact integer grid traversal (no sampling): it visits every tile the segment enters,
 * in order. Passing through (or within a couple of units of) a tile corner counts as a
 * diagonal step and requires both orthogonal neighbors to be open, so a segment can
 * never squeeze between two blocked tiles or graze a blocked corner.
 */
export function segmentClear(
  map: GameMap,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  extra?: OccupancyFn,
): boolean {
  const blocked = (tx: number, ty: number) =>
    isBlocked(map, tx, ty) || (extra !== undefined && extra(tx, ty));
  let tx = Math.floor(x0 / TILE);
  let ty = Math.floor(y0 / TILE);
  const ex = Math.floor(x1 / TILE);
  const ey = Math.floor(y1 / TILE);
  if (blocked(tx, ty)) return false;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const sx = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const sy = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  const len = isqrt(dx * dx + dy * dy);
  // Two boundary crossings closer than this along the segment count as a corner pass.
  const cornerTolerance = 2 * adx * ady;
  let guard = 0;
  while (tx !== ex || ty !== ey) {
    if (++guard > 4096) return false;
    // Distance-to-next-boundary along the segment, scaled by |dx|·|dy| so both are integers.
    const bx = sx > 0 ? (tx + 1) * TILE : tx * TILE;
    const by = sy > 0 ? (ty + 1) * TILE : ty * TILE;
    const ax = sx === 0 ? Infinity : Math.abs(bx - x0) * ady;
    const ay = sy === 0 ? Infinity : Math.abs(by - y0) * adx;
    if (ax === Infinity && ay === Infinity) return true;
    const diff = ax === Infinity || ay === Infinity ? Infinity : Math.abs(ax - ay) * len;
    if (diff <= cornerTolerance) {
      if (blocked(tx + sx, ty) || blocked(tx, ty + sy)) return false;
      tx += sx;
      ty += sy;
    } else if (ax < ay) {
      tx += sx;
    } else {
      ty += sy;
    }
    if (blocked(tx, ty)) return false;
  }
  return true;
}

/** Can a unit walk in a straight line between two tile centers without touching a blocked tile? */
export function lineOfSight(
  map: GameMap,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  extra?: OccupancyFn,
): boolean {
  return segmentClear(map, tileCenter(x0), tileCenter(y0), tileCenter(x1), tileCenter(y1), extra);
}

/**
 * String-pulling: keep only the waypoints the unit cannot see past.
 * `startWorld` is the unit's true position (it may be off its tile's center);
 * `path` excludes the start tile; the result does too.
 */
export function smoothPath(
  map: GameMap,
  startWorld: { readonly x: number; readonly y: number },
  path: readonly TilePoint[],
  extra?: OccupancyFn,
): TilePoint[] {
  const result: TilePoint[] = [];
  let cx = startWorld.x;
  let cy = startWorld.y;
  let i = 0;
  while (i < path.length) {
    let j = path.length - 1;
    while (
      j > i &&
      !segmentClear(map, cx, cy, tileCenter(path[j]!.x), tileCenter(path[j]!.y), extra)
    )
      j--;
    const next = path[j]!;
    result.push(next);
    cx = tileCenter(next.x);
    cy = tileCenter(next.y);
    i = j + 1;
  }
  return result;
}

/**
 * A* over the tile grid. One instance per map; scratch buffers are reused between
 * searches (stamped with a search id, so nothing is cleared per search).
 */
export class Pathfinder {
  private readonly map: GameMap;
  private readonly gCost: Int32Array;
  private readonly hCost: Int32Array;
  private readonly parent: Int32Array;
  private readonly seenStamp: Uint32Array;
  private readonly closedStamp: Uint32Array;
  private readonly seq: Int32Array;
  private readonly heap: Int32Array;
  private heapSize = 0;
  private stamp = 0;
  private seqCounter = 0;

  constructor(map: GameMap) {
    this.map = map;
    const n = map.width * map.height;
    this.gCost = new Int32Array(n);
    this.hCost = new Int32Array(n);
    this.parent = new Int32Array(n);
    this.seenStamp = new Uint32Array(n);
    this.closedStamp = new Uint32Array(n);
    this.seq = new Int32Array(n);
    this.heap = new Int32Array(n);
  }

  /**
   * Path from start to goal as tile waypoints (start excluded, goal included), already
   * smoothed. If the goal is blocked or unreachable, the path leads to the reachable
   * tile nearest the goal. Returns [] when the unit is already as close as it can get.
   * `maxExpansions` bounds the work on large maps; the best tile found so far is used.
   * `extra` marks additional blocked tiles (standing units) on top of the map.
   */
  findPath(
    startX: number,
    startY: number,
    goalX: number,
    goalY: number,
    extra?: OccupancyFn,
    maxExpansions = this.map.width * this.map.height,
    /** The unit's true position, for smoothing from an off-center start. Defaults to the start tile's center. */
    startWorld?: { readonly x: number; readonly y: number },
  ): TilePoint[] {
    const { map } = this;
    if (!inBounds(map, startX, startY)) return [];
    goalX = Math.max(0, Math.min(map.width - 1, goalX));
    goalY = Math.max(0, Math.min(map.height - 1, goalY));

    this.stamp++;
    this.heapSize = 0;
    this.seqCounter = 0;

    const start = startY * map.width + startX;
    const goal = goalY * map.width + goalX;
    this.gCost[start] = 0;
    this.hCost[start] = heuristic(startX, startY, goalX, goalY);
    this.parent[start] = -1;
    this.seenStamp[start] = this.stamp;
    this.push(start);

    let best = start;
    let expansions = 0;

    while (this.heapSize > 0 && expansions < maxExpansions) {
      const current = this.pop();
      if (current === goal) {
        best = goal;
        break;
      }
      this.closedStamp[current] = this.stamp;
      expansions++;

      const h = this.hCost[current]!;
      const bh = this.hCost[best]!;
      if (h < bh || (h === bh && this.gCost[current]! < this.gCost[best]!)) best = current;

      const cx = current % map.width;
      const cy = (current - cx) / map.width;
      for (const [dx, dy] of DIRECTIONS) {
        if (!canStep(map, cx, cy, dx, dy, extra)) continue;
        const nx = cx + dx;
        const ny = cy + dy;
        const n = ny * map.width + nx;
        if (this.closedStamp[n] === this.stamp) continue;
        const g = this.gCost[current]! + (dx !== 0 && dy !== 0 ? DIAGONAL : STRAIGHT);
        if (this.seenStamp[n] === this.stamp) {
          if (g >= this.gCost[n]!) continue;
          this.gCost[n] = g;
          this.parent[n] = current;
          this.decreaseKey(n);
        } else {
          this.seenStamp[n] = this.stamp;
          this.gCost[n] = g;
          this.hCost[n] = heuristic(nx, ny, goalX, goalY);
          this.parent[n] = current;
          this.push(n);
        }
      }
    }

    const raw: TilePoint[] = [];
    for (let n = best; n !== start; n = this.parent[n]!) {
      const x = n % map.width;
      raw.push({ x, y: (n - x) / map.width });
    }
    raw.reverse();
    return smoothPath(
      map,
      startWorld ?? { x: tileCenter(startX), y: tileCenter(startY) },
      raw,
      extra,
    );
  }

  // ---- binary min-heap keyed on (f, h, insertion order) ----

  private less(a: number, b: number): boolean {
    const fa = this.gCost[a]! + this.hCost[a]!;
    const fb = this.gCost[b]! + this.hCost[b]!;
    if (fa !== fb) return fa < fb;
    if (this.hCost[a] !== this.hCost[b]) return this.hCost[a]! < this.hCost[b]!;
    return this.seq[a]! < this.seq[b]!;
  }

  private push(n: number): void {
    this.seq[n] = this.seqCounter++;
    this.heap[this.heapSize] = n;
    this.siftUp(this.heapSize);
    this.heapSize++;
  }

  private pop(): number {
    const top = this.heap[0]!;
    this.heapSize--;
    if (this.heapSize > 0) {
      this.heap[0] = this.heap[this.heapSize]!;
      this.siftDown(0);
    }
    return top;
  }

  private decreaseKey(n: number): void {
    for (let i = 0; i < this.heapSize; i++) {
      if (this.heap[i] === n) {
        this.siftUp(i);
        return;
      }
    }
  }

  private siftUp(i: number): void {
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (!this.less(this.heap[i]!, this.heap[p]!)) return;
      const t = this.heap[i]!;
      this.heap[i] = this.heap[p]!;
      this.heap[p] = t;
      i = p;
    }
  }

  private siftDown(i: number): void {
    for (;;) {
      const l = 2 * i + 1;
      const r = l + 1;
      let m = i;
      if (l < this.heapSize && this.less(this.heap[l]!, this.heap[m]!)) m = l;
      if (r < this.heapSize && this.less(this.heap[r]!, this.heap[m]!)) m = r;
      if (m === i) return;
      const t = this.heap[i]!;
      this.heap[i] = this.heap[m]!;
      this.heap[m] = t;
      i = m;
    }
  }
}
